/**
 * social-approval.ts
 *
 * Pure, stateless approval state machine for the social post composer.
 * Driven by a per-org `require_approval` toggle.
 * No I/O — fully unit-testable.
 */

/** Full lifecycle status for a composed post (pre- and post-publish). */
export type PostStatus =
  | "draft"
  | "pending_approval"
  | "approved"
  | "scheduled"
  | "published"
  | "failed"
  | "canceled";

export type ComposeAction = "submit" | "approve" | "reject" | "to_draft";

export type ResolveResult =
  | { ok: true; next: PostStatus }
  | { ok: false; error: string };

/**
 * Statuses from which `to_draft` is allowed (pre-publish, actionable states).
 * published / failed / canceled are terminal or post-publish — disallowed.
 */
const TO_DRAFT_ALLOWED: PostStatus[] = [
  "draft",
  "pending_approval",
  "approved",
  "scheduled",
];

/**
 * Compute the next post status for a compose-time action, given the org's
 * require_approval toggle.
 *
 * Rules:
 *   - "submit" from "draft":
 *       requireApproval === true  -> "pending_approval"
 *       requireApproval === false -> "scheduled"
 *   - "approve" from "pending_approval" -> "scheduled"
 *   - "reject"  from "pending_approval" -> "draft"
 *   - "to_draft" from draft/pending_approval/approved/scheduled -> "draft"
 *   - "published" is terminal: every action returns an error.
 *   - Any other (status, action) pair returns an error.
 */
export function resolveComposeAction(
  currentStatus: PostStatus,
  action: ComposeAction,
  requireApproval: boolean,
): ResolveResult {
  // Terminal: published blocks everything
  if (currentStatus === "published") {
    return { ok: false, error: "Post is already published" };
  }

  switch (action) {
    case "submit": {
      if (currentStatus !== "draft") {
        return {
          ok: false,
          error: `Cannot submit from status '${currentStatus}'`,
        };
      }
      return {
        ok: true,
        next: requireApproval ? "pending_approval" : "scheduled",
      };
    }

    case "approve": {
      if (currentStatus !== "pending_approval") {
        return {
          ok: false,
          error: `Cannot approve from status '${currentStatus}'`,
        };
      }
      return { ok: true, next: "scheduled" };
    }

    case "reject": {
      if (currentStatus !== "pending_approval") {
        return {
          ok: false,
          error: `Cannot reject from status '${currentStatus}'`,
        };
      }
      return { ok: true, next: "draft" };
    }

    case "to_draft": {
      if (!TO_DRAFT_ALLOWED.includes(currentStatus)) {
        return {
          ok: false,
          error: `Cannot move to draft from status '${currentStatus}'`,
        };
      }
      return { ok: true, next: "draft" };
    }

    default: {
      const _exhaustive: never = action;
      return { ok: false, error: `Unknown action: ${_exhaustive}` };
    }
  }
}

/** The primary compose button, driven by the org toggle. */
export function composePrimaryAction(requireApproval: boolean): {
  label: string;
  action: ComposeAction;
} {
  return requireApproval
    ? { label: "Submit for review", action: "submit" }
    : { label: "Schedule", action: "submit" };
}
